import { FormElementEnum, RICSchema } from '../ricFormTypes';
import { FinalFeeType, InsuranceType } from '../../financingTypes';
import { ProductCategory } from '../../penTypes';

const WVRICSchema: RICSchema = {
  metadata: {
    feeTypeList: [
      FinalFeeType.GOVERNMENT_FEES_TAX,
      FinalFeeType.GOVERNMENT_REGISTRATION,
      FinalFeeType.TOTAL_GOVERNMENT_CERTIFICATE_TITLE,
      FinalFeeType.VEHICLE_LICENSE,
      FinalFeeType.OTHER,
    ],
    numInsuranceCreditDisability: 1,
    numInsuranceCreditLife: 1,
    numGapProducts: 1,
    numOtherDownpaymentElements: 3,
    numOtherFees: 2,
    numServiceContracts: 12,
    numTradeIns: 1,
    rebateAsOtherCharge: true,
  },
  elements: [
    {
      type: FormElementEnum.FORM_TITLE,
      title: 'Itemization of Amount Financed',
    },
    {
      type: FormElementEnum.SECTION,
      title: 'Vehicle Information',
      sectionElements: [
        {
          type: FormElementEnum.STRING_INPUT,
          title: 'Stock',
          responseDataField: 'carStock',
          required: true,
        },
        {
          type: FormElementEnum.INTEGER_INPUT,
          title: 'Mileage',
          responseDataField: 'mileage',
          required: true,
        },
      ],
    },
    {
      type: FormElementEnum.SECTION,
      title: 'Cash Price',
      sumValue: {
        responseDataFields: [
          'effectivePrice',
          'accessoriesPrice',
          'taxAmountOverride',
        ],
      },
      sectionElements: [
        {
          type: FormElementEnum.SUBSECTION,
          title: 'Cash Price of Motor Vehicle and Accessories',
          sumValue: {
            responseDataFields: ['effectivePrice', 'accessoriesPrice'],
          },
          subSectionElements: [
            {
              type: FormElementEnum.PRICE_INPUT,
              title: 'Cash Price of Motor Vehicle',
              responseDataField: 'effectivePrice',
              required: true,
            },
            {
              type: FormElementEnum.PRICE_INPUT,
              title: 'Cash Price of Accessories',
              responseDataField: 'accessoriesPrice',
            },
          ],
        },
        {
          type: FormElementEnum.PRICE_INPUT,
          title: 'Sales Tax',
          subtitle: '(On Taxable Items Listed Above)',
          responseDataField: 'taxAmountOverride',
        },
      ],
    },
    {
      type: FormElementEnum.SECTION,
      title: 'Total Downpayment',
      sumValue: {
        downPaymentWithMinZero: true,
      },
      sectionElements: [
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Trade-in Vehicle Year',
          tradeInField: 'year',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Trade-in Vehicle Make',
          tradeInField: 'make',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Trade-in Vehicle Model',
          tradeInField: 'model',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Gross Trade-In Allowance',
          subtitle: '(see trade-in vehicle)',
          tradeInField: 'tradeInAllowance',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Payoff Amount',
          tradeInField: 'payOff',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Less Pay Off Made By Seller To',
          tradeInField: 'currentLienHolder',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Equals Net Trade-In',
          tradeInField: 'netTrade',
          required: true,
        },
        {
          type: FormElementEnum.PRICE_INPUT,
          title: 'Cash',
          responseDataField: 'downPayment',
        },
        {
          type: FormElementEnum.CHARGE_TABLE,
          title: 'Other',
          displayTotalValue: false,
          responseDataField: 'otherDownPayments',
          columns: [
            {
              width: '75%',
              fieldName: 'description',
            },
          ],
        },
      ],
    },
    {
      type: FormElementEnum.LINE_ITEM,
      title: 'Unpaid Balance of Cash Price',
      sumValue: {
        responseDataFields: [
          'effectivePrice',
          'accessoriesPrice',
          'taxAmountOverride',
        ],
        subtractDownPaymentWithMinZero: true,
      },
    },
    {
      type: FormElementEnum.SECTION,
      title: 'Other Charges Including Amounts Paid to Others on Your Behalf',
      subtitle: '(Seller may keep part of these amounts)',
      sumValue: {
        feeTypes: [
          FinalFeeType.GOVERNMENT_FEES_TAX,
          FinalFeeType.GOVERNMENT_REGISTRATION,
          FinalFeeType.TOTAL_GOVERNMENT_CERTIFICATE_TITLE,
          FinalFeeType.VEHICLE_LICENSE,
          FinalFeeType.OTHER,
        ],
        insuranceTypes: [
          InsuranceType.CREDIT_LIFE,
          InsuranceType.CREDIT_DISABILITY,
        ],
        productCategories: [
          ProductCategory.SERVICE_CONTRACT,
          ProductCategory.GAP,
        ],
        downPaymentPayoff: true,
      },
      sectionElements: [
        {
          type: FormElementEnum.INSURANCE_TABLE,
          displayTotalValue: true,
          insuranceTypes: [
            InsuranceType.CREDIT_LIFE,
            InsuranceType.CREDIT_DISABILITY,
          ],
          title:
            'Cost of Optional Credit Insurance Paid to Insurance Company or Companies',
          columns: [
            {
              title: 'Term (months)',
              width: '200px',
              fieldName: 'termMonths',
            },
          ],
        },
        {
          type: FormElementEnum.PRODUCT_TABLE,
          title: 'Optional Gap Contract',
          displayTotalValue: true,
          productCategory: ProductCategory.GAP,
          columns: [
            {
              title: 'Name',
              width: '40%',
              fieldName: 'description',
            },
            {
              title: 'Term (months)',
              width: '20%',
              fieldName: 'months',
            },
          ],
        },
        {
          type: FormElementEnum.FEE_TABLE,
          title: 'Official Fees Paid to Government Agencies',
          displayTotalValue: false,
          feeType: FinalFeeType.OTHER,
          columns: [
            {
              width: '40%',
              fieldName: 'paidTo',
              title: 'To',
            },
            {
              width: '40%',
              fieldName: 'description',
              title: 'For',
            },
          ],
        },
        {
          type: FormElementEnum.FEE_INPUT,
          title: 'Government Taxes Not Included in Cash Price',
          feeType: FinalFeeType.GOVERNMENT_FEES_TAX,
        },
        {
          type: FormElementEnum.FEE_TABLE,
          title: 'Government License Fees',
          displayTotalValue: false,
          feeType: FinalFeeType.VEHICLE_LICENSE,
          columns: [
            {
              width: '75%',
              fieldName: 'description',
            },
          ],
        },
        {
          type: FormElementEnum.FEE_TABLE,
          title: 'Government Registration Fees',
          displayTotalValue: false,
          feeType: FinalFeeType.GOVERNMENT_REGISTRATION,
          columns: [
            {
              width: '75%',
              fieldName: 'description',
            },
          ],
        },
        {
          type: FormElementEnum.FEE_INPUT,
          title: 'Government Certificate of Title Fees',
          feeType: FinalFeeType.TOTAL_GOVERNMENT_CERTIFICATE_TITLE,
        },
        {
          type: FormElementEnum.SUBSECTION,
          title: 'Prior Credit or Lease Balance',
          sumValue: {
            downPaymentPayoff: true,
          },
        },
        {
          type: FormElementEnum.PRODUCT_TABLE,
          title: 'Other Charges',
          subtitle: '(Seller must identify who is paid and describe purpose)',
          displayTotalValue: true,
          productCategory: ProductCategory.SERVICE_CONTRACT,
          columns: [
            {
              title: 'To',
              width: '40%',
              fieldName: 'paidTo',
            },
            {
              title: 'For',
              width: '40%',
              fieldName: 'description',
            },
          ],
        },
      ],
    },
    {
      type: FormElementEnum.LINE_ITEM,
      title: 'Amount Financed',
      responseDataField: 'financeAmount',
    },
  ],
};

export default WVRICSchema;
